/**
 * Logs Storage Manager
 * 
 * Manages Docker logs storage in localStorage with the structure:
 * {
 *   logs: [
 *     {
 *       id: string,
 *       content: string,
 *       timestamp: string (ISO timestamp),
 *       backendId?: string,
 *       backendTs?: string
 *     }
 *   ]
 * }
 */

import { getFromStorage, saveToStorage } from '../../../../../shared/lib/storageUtils';

const STORAGE_KEY = 'dockerLogs';
const MAX_LOGS = 200; // Maximum number of logs to store

/**
 * Log entry stored in localStorage
 */
export interface StoredLogEntry {
    id: string;
    content: string;
    timestamp: string; // ISO string for storage
    backendId?: string; // Backend message ID (from payload.id)
    backendTs?: string; // Backend timestamp (from payload.ts)
}

/**
 * Log entry with Date object (for use in components)
 */
export interface LogEntry {
    id: string;
    content: string;
    timestamp: Date;
    backendId?: string;
    backendTs?: string;
}

export interface LogsStorage {
    logs: StoredLogEntry[];
}

/**
 * Get all logs from storage
 */
export function getLogsFromStorage(): LogsStorage {
    return getFromStorage<LogsStorage>(STORAGE_KEY) || { logs: [] };
}

/**
 * Add a new log entry to storage
 */
export function addLogEntry(logEntry: Omit<LogEntry, 'timestamp'> & { timestamp: Date | string }): void {
    const storage = getLogsFromStorage();
    
    // Check if log with same backendId already exists (skip if duplicate)
    if (logEntry.backendId) {
        const existingLog = storage.logs.find(log => log.backendId === logEntry.backendId);
        if (existingLog) {
            console.log(`⏭️ Skipping duplicate log entry with backendId: ${logEntry.backendId}`);
            return; // Skip adding this log
        }
    }
    
    // Convert timestamp to ISO string if it's a Date object
    const timestampISO = typeof logEntry.timestamp === 'string' 
        ? logEntry.timestamp 
        : logEntry.timestamp.toISOString();
    
    // Create stored log entry
    const storedLog: StoredLogEntry = {
        id: logEntry.id,
        content: logEntry.content,
        timestamp: timestampISO,
        backendId: logEntry.backendId,
        backendTs: logEntry.backendTs
    };
    
    // Add to logs array
    storage.logs.push(storedLog);
    
    // Keep only the last MAX_LOGS entries
    if (storage.logs.length > MAX_LOGS) {
        storage.logs = storage.logs.slice(-MAX_LOGS);
    }
    
    // Save to localStorage
    saveToStorage(STORAGE_KEY, storage);
    
    // Dispatch custom event to notify components of change
    window.dispatchEvent(new Event('dockerLogsChanged'));
}

/**
 * Add multiple log entries to storage
 */
export function addLogEntries(logEntries: Array<Omit<LogEntry, 'timestamp'> & { timestamp: Date | string }>): void {
    const storage = getLogsFromStorage();
    
    // Create a Set of existing backendIds for fast lookup
    const existingBackendIds = new Set(
        storage.logs
            .filter(log => log.backendId)
            .map(log => log.backendId)
    );
    
    // Filter out entries with duplicate backendIds
    const filteredEntries = logEntries.filter(entry => {
        if (entry.backendId && existingBackendIds.has(entry.backendId)) {
            console.log(`⏭️ Skipping duplicate log entry with backendId: ${entry.backendId}`);
            return false; // Skip this entry
        }
        // Add to set to prevent duplicates within the current batch
        if (entry.backendId) {
            existingBackendIds.add(entry.backendId);
        }
        return true;
    });
    
    // If all entries were duplicates, exit early
    if (filteredEntries.length === 0) {
        console.log('⏭️ All log entries were duplicates, skipping storage update');
        return;
    }
    
    // Convert filtered entries to stored format
    const storedLogs: StoredLogEntry[] = filteredEntries.map(entry => ({
        id: entry.id,
        content: entry.content,
        timestamp: typeof entry.timestamp === 'string' 
            ? entry.timestamp 
            : entry.timestamp.toISOString(),
        backendId: entry.backendId,
        backendTs: entry.backendTs
    }));
    
    // Add to logs array
    storage.logs.push(...storedLogs);
    
    // Keep only the last MAX_LOGS entries
    if (storage.logs.length > MAX_LOGS) {
        storage.logs = storage.logs.slice(-MAX_LOGS);
    }
    
    // Save to localStorage
    saveToStorage(STORAGE_KEY, storage);
    
    // Dispatch custom event to notify components of change
    window.dispatchEvent(new Event('dockerLogsChanged'));
}

/**
 * Get all logs with Date objects
 * @returns Array of LogEntry objects sorted by timestamp descending (newest first)
 */
export function getAllLogs(): LogEntry[] {
    const storage = getLogsFromStorage();
    
    // Convert stored logs to LogEntry objects with Date timestamps
    const logs: LogEntry[] = storage.logs.map(stored => ({
        id: stored.id,
        content: stored.content,
        timestamp: new Date(stored.timestamp),
        backendId: stored.backendId,
        backendTs: stored.backendTs
    }));
    
    // Sort by timestamp descending (newest first)
    return logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

/**
 * Clear all logs from storage
 */
export function clearLogsStorage(): void {
    localStorage.removeItem(STORAGE_KEY);
    // Dispatch custom event to notify components of change
    window.dispatchEvent(new Event('dockerLogsChanged'));
    console.log('🗑️ Cleared all Docker logs');
}

/**
 * Get the last received backend message ID
 */
export function getLastLogBackendId(): string | null {
    const logs = getAllLogs();
    if (logs.length === 0) return null;
    
    // Filter logs that have backend IDs and timestamps
    const logsWithBackendData = logs.filter(log => log.backendId && log.backendTs);
    
    if (logsWithBackendData.length === 0) return null;
    
    // Sort by backend timestamp (descending - latest first)
    const sortedLogs = [...logsWithBackendData].sort((a, b) => {
        const tsA = a.backendTs || '';
        const tsB = b.backendTs || '';
        return tsB.localeCompare(tsA);
    });
    
    // Return the backend ID from the latest log
    return sortedLogs[0].backendId || null;
}
